import { useEffect, useCallback } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import type { CloudId } from '@atlassian/jira-shared-types';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';

const HAS_TITAN_ACCESS_CACHE = new Map<
	CloudId,
	{ hasAccess?: boolean; request?: Promise<boolean> }
>();

export const getHasTitanAccess = async (cloudId: string): Promise<boolean> => {
	const response = await fetch(`/gateway/api/watermelon/workspace/exists?cloudId=${cloudId}`);
	return response.status === 200;
};

export type UseUserHasAccessToTitan = {
	hasPermission: boolean | undefined;
	loading: boolean;
	hasErrorOccurred: boolean;
	retry: () => void;
};

export const useUserHasAccessToTitan = (): UseUserHasAccessToTitan => {
	const cloudId = useCloudId();
	const checkAccess = useCallback(async () => {
		const cacheValue = HAS_TITAN_ACCESS_CACHE.get(cloudId);
		if (cacheValue?.hasAccess !== undefined) {
			return cacheValue.hasAccess;
		}

		if (cacheValue?.request !== undefined) {
			return cacheValue.request;
		}

		const request = getHasTitanAccess(cloudId);
		HAS_TITAN_ACCESS_CACHE.set(cloudId, { request });

		const hasAccess = await request;

		HAS_TITAN_ACCESS_CACHE.set(cloudId, { hasAccess });

		return hasAccess;
	}, [cloudId]);

	const {
		loading,
		error,
		data: hasPermission,
		fetch: fetchTitanAccess,
	} = useService<boolean>(checkAccess, { data: false });

	useEffect(() => {
		fetchTitanAccess();
	}, [fetchTitanAccess]);

	useEffect(() => {
		fireErrorAnalytics({
			meta: {
				id: 'fetchTitanPermission',
				packageName: 'jiraBusinessGoalPicker',
				teamName: 'jira-yurree',
			},
			sendToPrivacyUnsafeSplunk: true,
			error,
		});
	}, [error]);

	return {
		hasPermission,
		loading,
		hasErrorOccurred: !!error,
		retry: fetchTitanAccess,
	};
};
